<template>
  <div>
    <h1 class="mb-8 font-bold text-3xl">
      <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('my-orders')">My Orders</inertia-link>
      <span class="text-indigo-400 font-medium">/</span> View Order: #{{ order.id }}
    </h1>

    <div class="w-full xl:w-4/6 mb-4">
      <div class="bg-white shadow overflow-hidden sm:rounded-lg mb-4">
        <div class="px-4 py-5 sm:px-6">
          <div class="flex justify-between items-center">
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              {{ order.customer_account.name }}
            </h3>

            <span>
              <span class="text-sm leading-5 text-gray-500">Status: </span>
              <span class="uppercase text-sm leading-5 text-gray-900">{{ order.status_label }}</span>
            </span>
          </div>

          <p class="flex justify-between mt-1 text-sm leading-5 text-gray-500">
            <span v-if="order.customer_account.tin_number">TIN: {{ order.customer_account.tin_number }}</span>
            <span>Date: {{ order.created_at | localDateTime }}</span>
          </p>
        </div>
      </div>

      <CollectionNotice v-if="collectionData" :collection-data="collectionData"/>



      <!-- invoice panel -->
      <div v-if="order.invoice_ref" class="bg-white shadow sm:rounded-lg mt-3">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900">Invoice</h3>

          <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
            <div role="group">
              <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                <div>
                  <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                    Reference
                  </div>
                </div>
                <div class="mt-4 sm:mt-0 sm:col-span-2">
                  <div class="max-w-xs">
                    {{ order.invoice_ref }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
            <div role="group">
              <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                <div>
                  <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                    Amount
                  </div>
                </div>
                <div class="mt-4 sm:mt-0 sm:col-span-2">
                  <div class="max-w-xs">
                    {{ order.invoice_amount }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
            <div role="group">
              <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                <div>
                  <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                  </div>
                </div>
                <div class="mt-4 sm:mt-0 sm:col-span-2">
                  <div class="max-w-xs">
                    <div class="my-4"><a :href="route('my-orders.download-file', {order: order.id, file: 'invoice'})" target="_blank" class="btn-primary px-2.5 py-1.5">Download</a></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      <!-- end of invoice panel -->

      <!-- quotation & payment panel -->
      <div v-if="order.quotation_ref && order.status === 'quoted'" class="bg-white shadow sm:rounded-lg mt-3">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900">Quotation</h3>

          <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
            <div role="group">
              <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                <div>
                  <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                    Reference
                  </div>
                </div>
                <div class="mt-4 sm:mt-0 sm:col-span-2">
                  <div class="max-w-xs">
                    {{ order.quotation_ref }}
                  </div>
                </div>
              </div>
            </div>
          </div>


          <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4" v-if="order.quotation_file_path">
            <div role="group">
              <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                <div>
                  <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                    Quotation Attachment
                  </div>
                </div>
                <div class="mt-4 sm:mt-0 sm:col-span-2">
                  <div class="max-w-xs">
                    <a :href="route('my-orders.download-file', {order: order.id, file: 'quotation'})" target="_blank" class="btn-primary px-2.5 py-1.5">Download</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <template v-if="order.advance_payment">
            <template v-if="!order.customer_payment_transfer_slip_image_path">

              <div class="mt-8 border-t border-gray-200 pt-8 sm:mt-5 sm:pt-10">
                <div>
                  <h3 class="text-lg leading-6 font-medium text-gray-900">
                    Payment Information
                  </h3>
                  <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-500">
                    Transfer the above quoted amount to either of the following account and attach the transfer slip.
                  </p>

                  <div class="max-w-lg mt-3">
                    <div class="flex flex-col">
                      <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">

                          <div class="min-w-full shadow-m border bg-white px-2 py-1 rounded-md">

                            <div class="grid grid-cols-2 md:grid-cols-4" v-for="(account, account_index) in bankDetails" :key="account_index">
                              <div v-if="account_index === 0" class="uppercase text-xs leading-4 font-medium text-gray-500 order-1 md:order-1">Bank</div>
                              <div v-if="account_index === 0" class="uppercase text-xs leading-4 font-medium text-gray-500 order-3 md:order-2">Account Name</div>
                              <div v-if="account_index === 0" class="uppercase text-xs leading-4 font-medium text-gray-500 order-5 md:order-3">Currency</div>
                              <div v-if="account_index === 0" class="uppercase text-xs leading-4 font-medium text-gray-500 order-7 md:order-4">Account Number</div>

                              <div class="text-sm leading-5 font-medium text-gray-900 order-2 md:order-5">{{ account.bank }}</div>
                              <div class="text-sm leading-5 font-medium text-gray-900 order-4 md:order-6">{{ account.account_name }}</div>
                              <div class="text-sm leading-5 font-medium text-gray-900 order-6 md:order-7">{{ account.currency }}</div>
                              <div class="text-sm leading-5 font-medium text-gray-900 order-8 md:order-8">{{ account.account_number }}</div>
                            </div>

                          </div>

                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Payment Slip
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-xs">
                        <file-input :errors="$page.props.errors.payment_slip" v-model="form.payment_slip"></file-input>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Payment Transferred at
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-xs">
                        <date-input :errors="$page.props.errors.payment_transfer_at" v-model="form.payment_transfer_at"></date-input>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="text-center">
                <div class="mt-8 inline-flex">
                  <loading-button :loading="paymentUpdating" @click="updatePayment" class="btn-primary mr-2">Update Payment</loading-button>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Payment Slip
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-xs">
                        <a :href="route('my-orders.download-file', {order: order.id, file: 'payment-slip'})" target="_blank" class="btn-primary px-2.5 py-1.5">Download</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Payment Transferred at
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-xs">
                        {{ order.customer_payment_transfer_at }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </template>
          <template v-else-if="order.status === 'quoted'">
            <div class="text-center">
              <div class="mt-8 inline-flex">
                <loading-button :loading="accepting" @click="acceptQuotation" class="btn-primary mr-2">Accept Quotation</loading-button>
                <loading-button :loading="rejecting" @click="rejectQuotation" class="btn-red">Reject Quotation</loading-button>
              </div>
            </div>
          </template>

        </div>
      </div>

      <!-- end of quotation panel -->

    </div>

    <div class="w-full xl:w-4/6">
      <div class="bg-white shadow overflow-hidden sm:rounded-lg">
        <div class="px-4 py-5 sm:px-6">
          <h2 class="text-xl text-center mt-4 mb-10 font-medium">Order Details</h2>

          <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div class="sm:col-span-1">
              <dt class="text-sm leading-5 font-medium text-gray-500">
                Customer Reference:
              </dt>
              <dd class="mt-1 text-sm leading-5 text-gray-900">
                {{ order.customer_reference ? order.customer_reference : '-' }}
              </dd>
            </div>

            <div class="sm:col-span-1">
              <dt class="text-sm leading-5 font-medium text-gray-500">
                Created By:
              </dt>
              <dd class="mt-1 text-sm leading-5 text-gray-900">
                {{ `${order.created_by.first_name} ${order.created_by.last_name}` }}
              </dd>
            </div>

            <div class="sm:col-span-1">
              <dt class="text-sm leading-5 font-medium text-gray-500">
                Delivery method:
              </dt>
              <dd class="mt-1 text-sm leading-5 text-gray-900">
                {{ order.customer_to_collect === true ? 'Self Collection' : 'To be delivered' }}
              </dd>
            </div>

            <template v-if="order.customer_to_collect !== true">

              <div class="sm:col-span-1">
                <dt class="text-sm leading-5 font-medium text-gray-500">
                  Delivery Location:
                </dt>
                <dd class="mt-1 text-sm leading-5 text-gray-900">
                  {{ order.delivery_location }}
                </dd>
              </div>

              <div class="sm:col-span-1" v-if="order.delivery_location !== 'island'">
                <dt class="text-sm leading-5 font-medium text-gray-500">
                  Delivery Address:
                </dt>
                <dd class="mt-1 text-sm leading-5 text-gray-900">
                  {{ order.delivery_address }}
                </dd>
              </div>

              <template v-else>
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">
                    Vessel Name:
                  </dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    {{ order.vessel }}
                  </dd>
                </div>

                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">
                    Vessel Location:
                  </dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    {{ order.vessel_location }}
                  </dd>
                </div>
              </template>

            </template>

            <div class="sm:col-span-1">
              <dt class="text-sm leading-5 font-medium text-gray-500">
                Contact Number:
              </dt>
              <dd class="mt-1 text-sm leading-5 text-gray-900">
                {{ order.delivery_contact_number }}
              </dd>
            </div>


            <div class="sm:col-span-1">
              <dt class="text-sm leading-5 font-medium text-gray-500">
                Remarks:
              </dt>
              <dd class="mt-1 text-sm leading-5 text-gray-900">
                {{ order.remarks }}
              </dd>
            </div>
          </dl>

          <div class="my-4">
            <!-- order details table -->

            <div class="flex flex-col">
              <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div class="align-middle inline-block min-w-full overflow-hidden sm rounded-lg border border-gray-100">
                  <table class="min-w-full">
                    <thead>
                      <tr>
                        <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                          Code
                        </th>
                        <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                          Details
                        </th>
                        <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                          Unit
                        </th>
                        <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                          Qty
                        </th>
                        <th colspan="2" class="px-6 py-3 border-b border-gray-200 bg-gray-50" />
                      </tr>
                    </thead>

                    <tbody class="bg-white">
                      <tr v-for="item in order.items">
                        <td class="px-6 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-5 font-medium text-gray-900">
                          {{ item.item_code }}
                        </td>
                        <td class="px-6 py-4 border-b border-gray-200 text-sm leading-5 text-gray-500">
                          {{ item.description }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-5 text-gray-500">
                          {{ item.unit }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-5 text-gray-500">

                          <div v-if="order.status === 'open'">
                            {{ item.quantity }}
                          </div>
                          <div v-else>
                            <div v-if="item.quantity === item.quantity_approved">
                              {{ item.quantity }}
                            </div>
                            <div v-else>
                              <div class="text-sm line-through" v-if="item.quantity_approved < item.quantity">{{ item.quantity}}</div>
                              <div class="font-semibold">
                                {{ item.quantity_approved }}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right border-b border-gray-200 text-sm leading-5 font-medium">
                          <div v-if="order.approved_at !== null">
                            <span v-if="item.quantity === item.quantity_approved" class="text-teal-400 text-xs">OK</span>
                            <span v-if="item.quantity_approved > 0 && item.quantity > item.quantity_approved" class="text-yellow-600 text-xs">Limited</span>
                            <span v-if="item.quantity_approved == 0" class="text-pink-600 text-xs">N/A</span>
                          </div>
                        </td>

                        <td class="py-0 px-1 whitespace-nowrap border-b border-gray-200 text-sm text-gray-500">
                          <span title="completed" v-if="item.completed_at"><icon class="inline w-4 h-4 text-green-600" name="tick"></icon></span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- end of order details table -->

            <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 mt-4">
              <div class="sm:col-span-1">
                <dt class="text-sm leading-5 font-medium text-gray-500">
                  Last Updated:
                </dt>
                <dd class="mt-1 text-sm leading-5 text-gray-900 uppercase">
                  {{ order.updated_at }}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <OrderFulfillmentDetails :order-id="order.id" is-customer :warehouses="warehouseList"/>

      <div class="flex flex-row justify-between mt-3">
        <inertia-link class="text-indigo-100 hover:text-indigo-200 btn btn-indigo" :href="route('my-orders')">Go back to orders</inertia-link>
        <button v-if="canCancelOrder" class="btn-red" @click="modals.cancel = true">Cancel Order</button>
      </div>

      <!-- cancel modal -->
      <modal :show="modals.cancel" @closeModal="modals.cancel = false">
        <form @submit.prevent="cancelOrder">
          <div class="px-4 py-5 sm:p-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              Confirm to cancel your order
            </h3>
            <div class="mt-2 max-w-xl text-sm leading-5 text-gray-500">
              <p>
                Your order will be cancelled and this action cannot be reversed.
              </p>
            </div>
            <div class="mt-5">
              <loading-button type="submit" :loading="cancelling" class="inline-flex items-center justify-center px-4 py-2 border border-transparent font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-50 focus:outline-none focus:border-red-300 focus:shadow-outline-red active:bg-red-200 transition ease-in-out duration-150 sm:text-sm sm:leading-5">
                Cancel Order
              </loading-button>
            </div>
          </div>
        </form>
      </modal>
      <!-- end of cancel modal -->
    </div>
  </div>
</template>

<script>
  import Layout from '@/Shared/Customers/Layout'
  import LoadingButton from '@/Shared/LoadingButton'
  import FileInput from '@/Shared/FileInput'
  import DateInput from '@/Shared/DateInput'
  import CollectionNotice from './CollectionNotice'
  import Modal from '@/Shared/Modal'
  import OrderFulfillmentDetails from '@/Pages/Admin/CustomerOrders/OrderFulfillmentDetails'
  import Icon from '@/Shared/Icon'
  import PortalVue from "portal-vue"

  export default {
  name: 'Show',
  components: {OrderFulfillmentDetails, Modal, CollectionNotice, DateInput, FileInput, LoadingButton, Icon, PortalVue},
  layout: Layout,

  props: {
    order: {
      type: Object,
    },
    warehouseList: Array,
    bankDetails: Array,
    isCredit: Boolean,
    collectionNotice: {
      type: Object
    },
    collectionData: {
      type: Object
    }
  },

  data() {
    return {
      modals: {
        cancel: false
      },
      sending: false,
      accepting: false,
      rejecting: false,
      cancelling: false,
      paymentUpdating: false,
      form: {
        payment_slip: null,
        payment_transfer_at: null
      }
    }
  },

  computed: {
    canCancelOrder() {

      return this.order.status === 'open' || this.order.status === 'quoted'
    },
  },

  methods: {
    cancelOrder() {
      this.cancelling = true
      this.$inertia.post(this.route('my-orders.cancel', this.order.id)).then(() => this.cancelling = false)
    },

    updatePayment() {
      let formData = new FormData();
      formData.append('payment_slip', this.form.payment_slip);
      formData.append('payment_transfer_at', this.form.payment_transfer_at);
      formData.append('type', 'payment')
      formData.append('quotation_status', 'accepted')

      this.paymentUpdating = true
      this.$inertia.post(this.route('my-orders.update', this.order.id), formData).then(response => {
        this.paymentUpdating = false
      })
    },

    acceptQuotation() {
      this.accepting = true;
      this.$inertia.post(this.route('my-orders.update', this.order.id), {
        type: 'quotation_status',
        quotation_status: 'accepted'
      }).then(() => {
        this.accepting = false
      })
    },

    rejectQuotation() {
      this.rejecting = true;
      this.$inertia.post(this.route('my-orders.update', this.order.id), {
        type: 'quotation_status',
        quotation_status: 'rejected',
      }).then(() => {
        this.rejecting = false
      })
    }
  },
}
</script>

